import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { LoginScreen } from "./Auth/LoginScreen.ts";
import { RequireAuth } from "./Auth/RequireAuth.tsx";
import {
  AllFranchiseeScreenPath,
  FranchiseeScreen,
} from "./Franchisee/FranchiseeScreen.tsx";
import { AuthCtxProvider } from "./Auth/AuthCtxProvider.tsx";
import { ConfirmDialogProvider } from "./UI/Feedback/ConfirmDialogProvider.tsx";
import { ToastProvider } from "./UI/Feedback/ToastProvider.tsx";
import { AccessTokenScreen } from "./Debug/AccessTokenScreen.tsx";
import { useCreateApolloClient } from "./ApolloClient/useCreateApolloClient.ts";
import { ApolloProvider } from "@apollo/client";
import { useMemo } from "react";
import {
  CreateFranchiseeScreen,
  CreateFranchiseeScreenPath,
} from "./Franchisee/CreateFranchiseeScreen.tsx";
import {
  EditFranchiseeScreen,
  EditFranchiseeScreenPath,
} from "./Franchisee/EditFranchiseeScreen.tsx";
import { AllTopicScreenPath, TopicScreen } from "./Topic/TopicScreen.tsx";
import {
  CreateTopicScreen,
  CreateTopicScreenPath,
} from "./Topic/CreateTopicScreen.tsx";
import { EditTopicPath, EditTopicScreen } from "./Topic/EditTopicScreen.tsx";
import { AllUserScreen, AllUsersScreenPath } from "./User/AllUserScreen.tsx";
import {
  UserStatisticsScreen,
  UserStatisticsScreenPath,
} from "./User/Statistics/UserStatisticsScreen.tsx";
import { EditUserScreen, EditUserScreenPath } from "./User/EditUserScreen.tsx";
import {
  CreateUserScreen,
  CreateUserScreenPath,
} from "./User/CreateUserScreen.tsx";
import {
  LessonFeedbackReportScreen,
  LessonFeedbackReportScreenPath,
} from "./User/LessonFeedback/LessonFeedbackReportScreen.tsx";
import {
  AllUserClassRoomPlanScreen,
  AllUserClassRoomPlanScreenPath,
} from "./User/ClassRoomPlan/AllUserClassRoomPlanScreen.tsx";
import {
  CreateClassRoomPlanScreen,
  CreateClassRoomPlanScreenPath,
} from "./User/ClassRoomPlan/CreateClassRoomPlanScreen.tsx";
import { TestScreen, TestScreenPath } from "./TestScreen.tsx";
import {
  ClassRoomDetailScreen,
  ClassRoomDetailScreenPath,
} from "./ClassRoom/ClassRoomDetailScreen.tsx";
import {
  AvailabilityImportScreen,
  AvailabilityImportScreenPath,
} from "./Availability/AvailabilityImportScreen.tsx";
import {
  GenerateAvailabilityCsvScreen,
  GenerateAvailabilityCsvScreenPath,
} from "./Availability/GenerateAvailabilityCsvScreen.tsx";
import {
  AvailabilityReportScreen,
  AvailabilityReportScreenPath,
} from "./Availability/Report/AvailabilityReportScreen.tsx";
import {
  TeacherAvailabilityScreen,
  TeacherAvailabilityScreenPath,
} from "./Franchisee/TeacherAvailability/TeacherAvailabilityScreen.tsx";
import {
  SpeakingLevelChangeSuggestionScreen,
  SpeakingLevelChangeSuggestionScreenPath,
} from "./User/SpeakingLevelSuggestion/SpeakingLevelChangeSuggestionScreen.tsx";
import {
  AgGridClassRoomListPath,
  AgGridClassRoomListScreen,
} from "./ClassRoom/AgGridClassRoomListScreen.tsx";
import {
  CreateClassroomScreen,
  CreateClassroomScreenPath,
} from "./ClassRoom/CreateClassroom/CreateClassroomScreen.tsx";
import {
  UpdateClassRoomPlanScreen,
  UpdateClassRoomPlanScreenPath,
} from "./User/ClassRoomPlan/UpdateClassRoomPlanScreen.tsx";
import {
  UsersEligibleForWarrantyScreen,
  UsersEligibleForWarrantyScreenPath,
} from "./User/Warranty/UsersEligibleForWarrantyScreen.tsx";
import {
  CheckClosersAvailabilityScreen,
  CheckClosersAvailabilityScreenPath,
} from "./Closer/CheckClosersAvailabilityScreen.tsx";
import { CloserAvailabilityHistoryScreen } from "./Closer/History/CloserAvailabilityHistoryScreen.tsx";
import { CloserAvailabilityHistoryScreenPath } from "./Closer/History/closerAvailabilityHistoryPath.ts";
import { CloserStatsScreen } from "./Closer/Stats/CloserStatsScreen.tsx";
import { CloserStatsScreenPath } from "./Closer/Stats/closerStatsPath.ts";
import { CloserDetailScreen } from "./Closer/Detail/CloserDetailScreen.tsx";
import { CloserDetailScreenPath } from "./Closer/Detail/closerDetailPath.ts";
import { AssignmentsScreen } from "./Closer/Assignments/AssignmentsScreen.tsx";
import { AssignmentsScreenPath } from "./Closer/Assignments/assignmentsPath.ts";
import {
  AllUserGroupsScreen,
  AllUserGroupsScreenPath,
} from "./UserGroup/AllUserGroupsScreen.tsx";
import {
  CreateUserGroupScreen,
  CreateUserGroupScreenPath,
} from "./UserGroup/CreateUserGroupScreen.tsx";
import { AllProgressCheckPdfScreen } from "./ProgressCheckPdf/AllProgressCheckPdfScreen.tsx";
import { OnboardingLivesScreen } from "./OnboardingLive/OnboardingLivesScreen.tsx";
import { OnboardingLivePlanScreen } from "./OnboardingLive/OnboardingLivePlanScreen.tsx";
import { OnboardingLiveDetailScreen } from "./OnboardingLive/OnboardingLiveDetailScreen.tsx";
import {
  OnboardingLiveDetailScreenPath,
  OnboardingLivePlanScreenPath,
  OnboardingLivesScreenPath,
} from "./OnboardingLive/OnboardingLivePaths.ts";
import {
  EditUserGroupScreen,
  EditUserGroupScreenPath,
} from "./UserGroup/EditUserGroupScreen.tsx";
import {
  AllProgressCheckPdfScreenPath,
  CreateProgressCheckPdfScreenPath,
  EditProgressCheckPdfScreenPath,
} from "./ProgressCheckPdf/paths.ts";
import { CreateProgressCheckPdfScreen } from "./ProgressCheckPdf/CreateProgressCheckPdfScreen.tsx";
import { EditProgressCheckPdfScreen } from "./ProgressCheckPdf/EditProgressCheckPdfScreen.tsx";
import {
  CreateOneOnOneAssignmentPdfScreenPath,
  EditOneOnOneAssignmentPdfScreenPath,
} from "./OneOnOneAssignmentPdf/paths.ts";
import { CreateOneOnOneAssignmentPdfScreen } from "./OneOnOneAssignmentPdf/CreateOneOnOneAssignmentPdfScreen.tsx";
import { EditOneOnOneAssignmentPdfScreen } from "./OneOnOneAssignmentPdf/EditOneOnOneAssignmentPdfScreen.tsx";
import {
  IssuedGiftCardsScreen,
  IssuedGiftCardsScreenPath,
} from "./IssuedGiftCard/IssuedGiftCardsScreen.tsx";
import {
  ReferredUsersScreen,
  ReferredUsersScreenPath,
} from "./Referral/ReferredUsersScreen.tsx";
import {
  CreateBulkClassRoomScreen,
  CreateBulkClassRoomScreenPath,
} from "./BulkClassRoom/CreateBulkClassRoomScreen.tsx";
import {
  BulkClassRoomIntentDetailScreen,
  BulkClassRoomIntentDetailScreenPath,
} from "./BulkClassRoom/BulkClassRoomIntentDetailScreen.tsx";
import {
  BulkClassRoomCreationRequestsScreen,
  BulkClassRoomCreationRequestsScreenPath,
} from "./BulkClassRoom/BulkClassRoomCreationRequestsScreen.tsx";
import {
  BulkClassRoomCreationRequestDetailScreen,
  BulkClassRoomCreationRequestDetailScreenPath,
} from "./BulkClassRoom/BulkClassRoomCreationRequestDetailScreen.tsx";
import {
  UnredeemedPlansScreen,
  UnredeemedPlansScreenPath,
} from "./BulkClassRoom/UnredeemedPlansScreen.tsx";
import {
  AllUser1on1ClassroomPlansScreen,
  AllUser1on1ClassroomPlansScreenPath,
} from "./User/OneOnOne/AllUser1on1ClassroomPlansScreen.tsx";
import {
  OneOnOnePlanPurchasesScreen,
  OneOnOnePlanPurchasesScreenPath,
} from "./OneOnOnePlanPurchase/OneOnOnePlanPurchasesScreen.tsx";
import {
  TeacherCoordinatorReviewScreen,
  TeacherCoordinatorReviewScreenPath,
} from "./Franchisee/TeacherCoordinatorReview/TeacherCoordinatorReviewScreen.tsx";
import {
  TeacherMissingFeedbackScreen,
  TeacherMissingFeedbackScreenPath,
} from "./Franchisee/TeacherMissingFeedback/TeacherMissingFeedbackScreen.tsx";
import {
  LegacyTeacherDetailScreenPath,
  RedirectToTeacherDetailScreen,
  TeacherDetailScreen,
  TeacherDetailScreenPath,
} from "./Franchisee/TeacherDetailScreen.tsx";
import {
  BookedClassRoomsScreen,
  BookedClassRoomsScreenPath,
} from "./BookedClassRooms/BookedClassRoomsScreen.tsx";
import {
  UserClassRoomHistoryScreen,
  UserClassRoomHistoryScreenPath,
} from "./User/ClassRoomHistory/UserClassRoomHistoryScreen.tsx";

function App() {
  const { createApolloClient } = useCreateApolloClient();
  const apolloClient = useMemo(
    () => createApolloClient(),
    [createApolloClient],
  );
  return (
    <ApolloProvider client={apolloClient}>
      <AuthCtxProvider>
        <ToastProvider>
          <ConfirmDialogProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/login" element={<LoginScreen />} />
                <Route
                  path="/"
                  element={<Navigate to={AllFranchiseeScreenPath} replace />}
                />
                <Route
                  path={AllFranchiseeScreenPath}
                  element={
                    <RequireAuth>
                      <FranchiseeScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={CreateFranchiseeScreenPath}
                  element={
                    <RequireAuth>
                      <CreateFranchiseeScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={EditFranchiseeScreenPath}
                  element={
                    <RequireAuth>
                      <EditFranchiseeScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={AllTopicScreenPath}
                  element={
                    <RequireAuth>
                      <TopicScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={CreateTopicScreenPath}
                  element={
                    <RequireAuth>
                      <CreateTopicScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={AllUsersScreenPath}
                  element={
                    <RequireAuth>
                      <AllUserScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={UserStatisticsScreenPath}
                  element={
                    <RequireAuth>
                      <UserStatisticsScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={EditUserScreenPath}
                  element={
                    <RequireAuth>
                      <EditUserScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={CreateUserScreenPath}
                  element={
                    <RequireAuth>
                      <CreateUserScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={EditTopicPath}
                  element={
                    <RequireAuth>
                      <EditTopicScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={LessonFeedbackReportScreenPath}
                  element={
                    <RequireAuth>
                      <LessonFeedbackReportScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={AllUserClassRoomPlanScreenPath}
                  element={
                    <RequireAuth>
                      <AllUserClassRoomPlanScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={CreateClassRoomPlanScreenPath}
                  element={
                    <RequireAuth>
                      <CreateClassRoomPlanScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={AgGridClassRoomListPath}
                  element={
                    <RequireAuth>
                      <AgGridClassRoomListScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={CreateClassroomScreenPath}
                  element={
                    <RequireAuth>
                      <CreateClassroomScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={AllUserGroupsScreenPath}
                  element={
                    <RequireAuth>
                      <AllUserGroupsScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={OnboardingLivesScreenPath}
                  element={
                    <RequireAuth>
                      <OnboardingLivesScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={OnboardingLivePlanScreenPath}
                  element={
                    <RequireAuth>
                      <OnboardingLivePlanScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={OnboardingLiveDetailScreenPath}
                  element={
                    <RequireAuth>
                      <OnboardingLiveDetailScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={EditUserGroupScreenPath}
                  element={
                    <RequireAuth>
                      <EditUserGroupScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={AllProgressCheckPdfScreenPath}
                  element={
                    <RequireAuth>
                      <AllProgressCheckPdfScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={CreateProgressCheckPdfScreenPath}
                  element={
                    <RequireAuth>
                      <CreateProgressCheckPdfScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={EditProgressCheckPdfScreenPath}
                  element={
                    <RequireAuth>
                      <EditProgressCheckPdfScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={CreateOneOnOneAssignmentPdfScreenPath}
                  element={
                    <RequireAuth>
                      <CreateOneOnOneAssignmentPdfScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={EditOneOnOneAssignmentPdfScreenPath}
                  element={
                    <RequireAuth>
                      <EditOneOnOneAssignmentPdfScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={CreateUserGroupScreenPath}
                  element={
                    <RequireAuth>
                      <CreateUserGroupScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={ClassRoomDetailScreenPath}
                  element={
                    <RequireAuth>
                      <ClassRoomDetailScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={AvailabilityImportScreenPath}
                  element={
                    <RequireAuth>
                      <AvailabilityImportScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={GenerateAvailabilityCsvScreenPath}
                  element={
                    <RequireAuth>
                      <GenerateAvailabilityCsvScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={AvailabilityReportScreenPath}
                  element={
                    <RequireAuth>
                      <AvailabilityReportScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={TeacherAvailabilityScreenPath}
                  element={
                    <RequireAuth>
                      <TeacherAvailabilityScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={UpdateClassRoomPlanScreenPath}
                  element={
                    <RequireAuth>
                      <UpdateClassRoomPlanScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={SpeakingLevelChangeSuggestionScreenPath}
                  element={
                    <RequireAuth>
                      <SpeakingLevelChangeSuggestionScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={UsersEligibleForWarrantyScreenPath}
                  element={
                    <RequireAuth>
                      <UsersEligibleForWarrantyScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={CheckClosersAvailabilityScreenPath}
                  element={
                    <RequireAuth>
                      <CheckClosersAvailabilityScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={CloserAvailabilityHistoryScreenPath}
                  element={
                    <RequireAuth>
                      <CloserAvailabilityHistoryScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={CloserStatsScreenPath}
                  element={
                    <RequireAuth>
                      <CloserStatsScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={CloserDetailScreenPath}
                  element={
                    <RequireAuth>
                      <CloserDetailScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={AssignmentsScreenPath}
                  element={
                    <RequireAuth>
                      <AssignmentsScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={IssuedGiftCardsScreenPath}
                  element={
                    <RequireAuth>
                      <IssuedGiftCardsScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={ReferredUsersScreenPath}
                  element={
                    <RequireAuth>
                      <ReferredUsersScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={CreateBulkClassRoomScreenPath}
                  element={
                    <RequireAuth>
                      <CreateBulkClassRoomScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={UnredeemedPlansScreenPath}
                  element={
                    <RequireAuth>
                      <UnredeemedPlansScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={BulkClassRoomIntentDetailScreenPath}
                  element={
                    <RequireAuth>
                      <BulkClassRoomIntentDetailScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={BulkClassRoomCreationRequestsScreenPath}
                  element={
                    <RequireAuth>
                      <BulkClassRoomCreationRequestsScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={BulkClassRoomCreationRequestDetailScreenPath}
                  element={
                    <RequireAuth>
                      <BulkClassRoomCreationRequestDetailScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={UnredeemedPlansScreenPath}
                  element={
                    <RequireAuth>
                      <UnredeemedPlansScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={AllUser1on1ClassroomPlansScreenPath}
                  element={
                    <RequireAuth>
                      <AllUser1on1ClassroomPlansScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={OneOnOnePlanPurchasesScreenPath}
                  element={
                    <RequireAuth>
                      <OneOnOnePlanPurchasesScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/token"
                  element={
                    <RequireAuth>
                      <AccessTokenScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={TeacherCoordinatorReviewScreenPath}
                  element={
                    <RequireAuth>
                      <TeacherCoordinatorReviewScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={TeacherMissingFeedbackScreenPath}
                  element={
                    <RequireAuth>
                      <TeacherMissingFeedbackScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={TeacherDetailScreenPath}
                  element={
                    <RequireAuth>
                      <TeacherDetailScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={LegacyTeacherDetailScreenPath}
                  element={<RedirectToTeacherDetailScreen />}
                />
                <Route
                  path={BookedClassRoomsScreenPath}
                  element={
                    <RequireAuth>
                      <BookedClassRoomsScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path={UserClassRoomHistoryScreenPath}
                  element={
                    <RequireAuth>
                      <UserClassRoomHistoryScreen />
                    </RequireAuth>
                  }
                />
                <Route path={TestScreenPath} element={<TestScreen />} />
              </Routes>
            </BrowserRouter>
          </ConfirmDialogProvider>
        </ToastProvider>
      </AuthCtxProvider>
    </ApolloProvider>
  );
}

export default App;
